import { useEffect, useRef, useState } from "react";

/**
 * Count-up for the network proof numbers (trust bar, price block, article
 * sidebar). Ported from www-medifyme `src/components/stats/useCountUp.js`.
 *
 * 🔴 The numbers are NOT produced by this hook. They arrive already formatted
 * from config/trust.ts, are rendered into the HTML, and the hook only replaces
 * them for the ~1.2 s the animation lasts. A crawler, a screenshot, a failed
 * bundle or a browser without IntersectionObserver all see the true figures,
 * because at no point does the markup start at zero.
 *
 * The final frame is re-assembled with the SAME grouping character as the
 * source string, so "72 000+" counts up to "72 000+", never to "72000" or
 * "72,000".
 */

/* A run of digits, optionally grouped by a space of some kind. Matches the
   "72 000" inside "72 000+" and the "100" inside "100+". */
const DIGIT_RUN = /\d(?:\s?\d)*/;

/** Milliseconds for one cell to travel from zero to its value. */
export const COUNT_DURATION = 1200;

/** Milliseconds each cell waits behind the previous one. */
export const COUNT_STAGGER = 90;

/* Decelerating ease: a counter that creeps into its final value reads as
   settling rather than stopping. */
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

type Parsed = { target: number; render: (value: number) => string };

/**
 * Take a display string apart so its digits can be animated and re-printed in
 * the identical format. Returns null for anything with no digits to count.
 */
export function parseValue(text: string): Parsed | null {
  const match = DIGIT_RUN.exec(text);
  if (!match) return null;

  const raw = match[0];
  const separator = (raw.match(/\s/) || [""])[0];
  const target = Number(raw.replace(/\s/g, ""));
  if (!Number.isFinite(target)) return null;

  const prefix = text.slice(0, match.index);
  const suffix = text.slice(match.index + raw.length);
  /* Group with the separator lifted from the source, not with a locale guess:
     toLocaleString would silently hand back U+00A0 instead of the plain space
     trust.ts uses. */
  const group = (value: number) =>
    separator ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator) : String(value);

  return { target, render: (value) => prefix + group(value) + suffix };
}

/**
 * Animate a row of already-formatted figures once, when it scrolls into view.
 *
 * A null entry is passed through untouched — for a cell that must not count,
 * such as a rating ("4,9" would count to 4 and then jump) or "24/7".
 */
export function useCountUp<T extends HTMLElement = HTMLDivElement>(
  values: Array<string | null>,
  { duration = COUNT_DURATION, stagger = COUNT_STAGGER } = {}
) {
  const ref = useRef<T>(null);
  /* Server render and first client render are the final values — see the file
     header. Nothing below runs before mount. */
  const [display, setDisplay] = useState<Array<string | null>>(values);
  /* Callers build the array inline on every render; keying the effect on its
     content keeps it from restarting the animation on each re-render. */
  const key = values.join("|");

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    /* A reader who asks for less motion gets the numbers outright. */
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const parsed = values.map((value) => (value == null ? null : parseValue(value)));
    if (!parsed.some(Boolean)) return undefined;

    let frame = 0;
    let startedAt = 0;

    const step = (now: number) => {
      if (!startedAt) startedAt = now;
      const elapsed = now - startedAt;

      setDisplay(
        parsed.map((entry, index) => {
          if (!entry) return values[index];
          const progress = Math.min(1, Math.max(0, (elapsed - index * stagger) / duration));
          return entry.render(Math.round(entry.target * easeOut(progress)));
        })
      );

      const total = duration + (parsed.length - 1) * stagger;
      if (elapsed < total) {
        frame = requestAnimationFrame(step);
      } else {
        /* Land on the original strings by identity, so the last frame cannot
           differ from the markup by even one character. */
        setDisplay(values);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.35 }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, duration, stagger]);

  return { ref, values: display };
}
